import { Megaphone, Settings } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { DialogFooter } from "~/components/ui/dialog";
import { Form } from "~/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

const GOOGLE_ADS_SETUP_LOCAL_STORAGE_KEY = "hide_google_ads_setup";

interface GoogleAdsSetupInstructionsProps {
  onFinish: () => void;
}

export function GoogleAdsSetupInstructions({
  onFinish,
}: GoogleAdsSetupInstructionsProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const form = useForm();

  const handleDontShowAgainChange = (checked: boolean) => {
    setDontShowAgain(checked);
    localStorage.setItem(
      GOOGLE_ADS_SETUP_LOCAL_STORAGE_KEY,
      checked.toString(),
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-sm">
        <p className="mb-1.5">
          To track Google Ads traffic, you need to add the UTM parameter{" "}
          <code className="text-[13px] bg-neutral-200/60 px-1 py-px rounded-sm">{`utm_term={keyword}`}</code>{" "}
          to your Google Ads account or campaign.
        </p>

        <p className="mb-3">
          To track all campaigns, configure it on account level. To track
          specific campaigns, configure it on campaign level.
        </p>

        <Tabs
          className="border border-border rounded-md p-2"
          defaultValue="account-level"
        >
          <TabsList className="w-full">
            <TabsTrigger value="account-level">Account config</TabsTrigger>
            <TabsTrigger value="campaign-level">Campaign config</TabsTrigger>
          </TabsList>

          <TabsContent value="account-level" className="px-1 space-y-2">
            <ol className="list-decimal list-outside pl-3.5 space-y-1">
              <li className="pl-1">Sign in to your Google Ads account</li>
              <li className="pl-1">
                Click on{" "}
                <strong>
                  <Settings
                    size={14}
                    className="inline -translate-y-0.5 text-muted-foreground"
                  />{" "}
                  Admin
                </strong>{" "}
                on the left sidebar
              </li>
              <li className="pl-1">
                Select <strong>Account Settings</strong>
              </li>
              <li className="pl-1">
                Select the <strong>Tracking</strong> tab
              </li>
              <li className="pl-1">
                <p>
                  In the <strong>Final URL suffix</strong> field set:{" "}
                  <code className="text-[13px] bg-neutral-200/60 px-1 py-px rounded-sm">{`utm_term={keyword}`}</code>
                  .
                </p>
                <p className="mt-1">
                  If you already have a tracking template, append{" "}
                  <code className="text-[13px] bg-neutral-200/60 px-1 py-px rounded-sm">{`&utm_term={keyword}`}</code>{" "}
                  to it.
                </p>
              </li>

              <li className="pl-1">
                <p>
                  Click <strong>Save</strong>
                </p>
              </li>
            </ol>
          </TabsContent>

          <TabsContent value="campaign-level" className="px-1 space-y-2">
            <ol className="list-decimal list-outside pl-3.5 space-y-1">
              <li className="pl-1">Sign in to your Google Ads account</li>
              <li className="pl-1">
                Click on{" "}
                <strong>
                  <Megaphone
                    size={14}
                    className="inline -translate-y-0.5 text-muted-foreground"
                  />{" "}
                  Campaigns
                </strong>{" "}
                in the left sidebar
              </li>
              <li className="pl-1">
                Select the campaign you want to configure
              </li>
              <li className="pl-1">
                Click on <strong>Settings</strong> in the left sidebar
              </li>
              <li className="pl-1">
                Scroll down to <strong>Campaign URL options</strong> and click{" "}
                <strong>Edit</strong>
              </li>
              <li className="pl-1">
                <p>
                  In the <strong>Tracking template</strong> field, add:{" "}
                  <code className="text-[13px] bg-neutral-200/60 px-1 py-px rounded-sm">{`{lpurl}?utm_term={keyword}`}</code>
                  .
                </p>
                <p className="mt-1">
                  If you already have a tracking template, append{" "}
                  <code className="text-[13px] bg-neutral-200/60 px-1 py-px rounded-sm">{`&utm_term={keyword}`}</code>{" "}
                  to it.
                </p>
              </li>
              <li className="pl-1">
                <p>
                  Click <strong>Save</strong>
                </p>
              </li>
            </ol>
          </TabsContent>
        </Tabs>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onFinish)}>
          <DialogFooter className="flex items-center justify-between">
            <div className="w-full flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="dont-show-again"
                  checked={dontShowAgain}
                  onCheckedChange={handleDontShowAgainChange}
                />
                <label
                  htmlFor="dont-show-again"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Don't show this again
                </label>
              </div>
              <Button type="button" onClick={onFinish}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
